import { TitleCasePipe } from "@angular/common";
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ConfirmationService, MessageService } from "primeng/api";
import { AppMainService } from "src/app/app-main.service";
import { AppMainComponent } from "src/app/app.main.component";
import { Severity, Status } from "src/app/inventory/constants/constants";
import { PurchasesService } from "src/app/inventory/purchases/purchases.service";
import { CurrencyService } from "src/app/modules/shared/services/currency.service";
import { Subject, lastValueFrom, takeUntil } from "rxjs";
import { ITransferFund } from "src/app/finance/models/transfer-fund.model";
import { FinanceService } from "src/app/finance/finance.service";
import { FinanceUtilityService } from "src/app/finance/util/finance-utility.service";
import { ImportserialService } from "../../services/importserial.service";
import { ClosingYearDisplayMessages } from "src/app/finance/constants/closing-year.constant";
import { UtilityService } from "src/app/inventory/sales-order/utils/utility.service";
import { IPostingPeriodDetails } from "../posting-period-selector/models/posting-period.model";

@Component({
    selector: "app-transfer-funds",
    templateUrl: "./transfer-funds.component.html",
    styleUrls: ["./transfer-funds.component.scss"],
})
export class TransferFundsComponent implements OnInit, OnChanges, OnDestroy {
    @Input() display;
    @Input() selectedFinance;
    @Input() transferFundId;
    @Input() dialogType;
    @Input() isShowVoidDelete : boolean = true;
    @Input() isEditableViewDialog : boolean = true;
    @Output() close = new EventEmitter();
    protected ngUnsubscribe$: Subject<void> = new Subject<void>();

    // banks = [];
    // selectedBank;
    fromAccounts = [];
    toAccounts = [];
    financialAccounts = [];
    transferFundForm: FormGroup;
    fromAccountEndingBalance = 0;
    currencySymbol = "";
    transferFundDetails;
    fundTransferHeader: string = "Transfer Funds";
    isVoided: boolean = false;
    isAllowPostingPeriod: boolean = false;
    currentPostingPeriodInfo: IPostingPeriodDetails;
    updatedPostingPeriodInfo: IPostingPeriodDetails;

    accountLabels: string[] = ["assets", "account receivable", "account payable", "liabilities", "equity"];
    // fraction digits
    fractionDigits: number = 0;
    statementMessage: string = "";
  
    constructor(
        private confirmationService: ConfirmationService,
        public app: AppMainComponent,
        private financeService: FinanceService,
        private appMainService: AppMainService,
        private messageService: MessageService,
        private titlecasePipe: TitleCasePipe,
        private fb: FormBuilder,
        private currencyService: CurrencyService,
        private financeUtilityService: FinanceUtilityService,
        private importService: ImportserialService,
        private utilityService: UtilityService,
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.selectedFinance && changes.selectedFinance.currentValue) {
            this.selectedFinance = changes.selectedFinance.currentValue;
            this.loadFinancialAccounts();
        }
        if (changes.transferFundId && changes.transferFundId.currentValue) {
            this.transferFundId = changes.transferFundId.currentValue;
            this.getFundTransferDetails(this.transferFundId);
        }
    }

    ngOnInit(): void {
        this.getIsetting();
        this.getFractionDigits();
        this.initTransferFundForm();
        this.currencySymbol = this.currencyService.currencySymbolValue;
    }
    
    getIsetting(){
        this.appMainService.isShowSpinner("loadISettings");
        const iSettings = this.utilityService.getISettings();
        this.isAllowPostingPeriod = iSettings?.find(obj => obj.code === 'posting')?.enabled ?? false;
        this.appMainService.isHideSpinner("loadISettings");
    }

    ngOnDestroy(): void {
        if (this.ngUnsubscribe$) {
            this.ngUnsubscribe$.next();
            this.ngUnsubscribe$.complete();
            this.ngUnsubscribe$.unsubscribe();
        }
    }

    initTransferFundForm() {
        this.transferFundForm = this.fb.group({
            id: [0],
            fromFinancialAccountId: ["", Validators.required],
            toFinancialAccountId: ["", Validators.required],
            transferDate: [this.app._showDateTimeWithZone(new Date()), Validators.required],
            transferAmount: ["", Validators.required],
            memo: [""],
        });
    }

    getFractionDigits() {
        this.importService
            .getDecimalValue()
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: (value) => {
                    if (value) {
                        this.fractionDigits = value;
                    }
                },
                error: (error) => { },
            });
    }

    getFundTransferDetails(id: number) {
        this.initTransferFundForm();
        this.appMainService.isShowSpinner("loaddetails");
        this.financeService.getFundTransferDetails(id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: async (res) => {
                    this.transferFundDetails = res;
                    this.isVoided = res?.isVoided
                    const accounts = await lastValueFrom(this.financeService.getAllFinancialAccounts(false));
                    this.financialAccounts = this.financeUtilityService.prepareFinancialAccountsData(accounts);
                    this.prepareAccountsData(this.financialAccounts);

                    this.transferFundForm.patchValue({
                        ...this.transferFundDetails,
                        transferDate: this.app._showDateTimeWithZone(this.transferFundDetails.transferDate),
                        fromFinancialAccountId: this.financeUtilityService.findRecursiveChildObjects(this.fromAccounts, this.transferFundDetails?.fromFinancialAccountId),
                        toFinancialAccountId: this.financeUtilityService.findRecursiveChildObjects(this.toAccounts, this.transferFundDetails?.toFinancialAccountId)
                    });
                    this.currentPostingPeriodInfo = {
                        postingDate: this.transferFundDetails?.postingDate,
                        postingMonth: this.transferFundDetails?.postingMonth,
                        postingYear: this.transferFundDetails?.postingYear
                    };
                    this.transferFundForm.disable();
                    this.getEndingBalance(this.transferFundDetails?.fromFinancialAccountId);
                    this.appMainService.isHideSpinner("loaddetails");
                },
                error: (error) => {
                    this.appMainService.isHideSpinner("loaddetails");
                    this.messageService.add({
                        severity: Severity.Error,
                        summary: this.titlecasePipe.transform(Severity.Error),
                        detail: "Error occurred ...",
                    });
                },
            });
    }
    loadFinancialAccounts() {
        this.appMainService.isShowSpinner("loadaccounts");
        this.financeService.getAllFinancialAccounts(false)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: (accounts) => {
                    this.appMainService.isHideSpinner("loadaccounts");
                    this.financialAccounts = this.financeUtilityService.prepareFinancialAccountsData(accounts);
                    this.prepareAccountsData(this.financialAccounts);
                    this.setDefaultAccount();
                },
                error: (error) => {
                    this.appMainService.isHideSpinner("loadaccounts");
                    this.messageService.add({
                        severity: Severity.Error,
                        summary: this.titlecasePipe.transform(Severity.Error),
                        detail: "Error occurred ...",
                    });
                },
            });
    }

    prepareAccountsData(accounts) {
        // let assetData = accounts?.find((a) => a?.label?.toLowerCase() == "assets");
        // let bankData = assetData?.children?.find((b) => b?.label?.toLowerCase() == "bank");
        // this.fromAccounts = bankData?.children && bankData?.children?.length > 0 ? JSON.parse(JSON.stringify(bankData?.children)) : [];
        // this.toAccounts =  bankData?.children && bankData?.children?.length > 0 ? JSON.parse(JSON.stringify(bankData?.children)): [];

        let fAccounts = accounts.filter((a) => this.accountLabels.includes(a.name.toLowerCase()));
        this.fromAccounts = fAccounts;
        this.toAccounts = fAccounts;
    }
    setDefaultAccount() {
        let selectedBank;
        if (this.selectedFinance.name?.toLowerCase() == "bank" && this.selectedFinance?.id == 0) {
            // selectedBank = this.fromAccounts[0];
        } else {
            selectedBank = this.financeUtilityService.findRecursiveChildObjects(this.fromAccounts, this.selectedFinance?.id);
        }

        if (selectedBank) {
            this.transferFundForm.controls.fromFinancialAccountId.setValue(selectedBank);
            this.getEndingBalance(selectedBank?.id);
            this.disableToAccount(selectedBank?.id);
        }
    }

    save() {
        if (!!this.statementMessage) { return; }
        let transferFundObj: ITransferFund = {
            id: this.transferFundId ? this.transferFundId : 0,
            createdSource: this.app.userIP,
            updatedSource: this.app.userIP,
            createdBy: this.app.user.userId,
            updatedBy: this.app.user.userId,
            createdDate: this.app._saveDateTimeWithZone(new Date()),
            updatedDate: this.app._saveDateTimeWithZone(new Date()),
            organizationId: this.app.companyId,
            fromFinancialAccountId: this.transferFundForm.controls.fromFinancialAccountId?.value?.value?.id ?? 0,
            fromAccountBalance: this.fromAccountEndingBalance,
            toFinancialAccountId: this.transferFundForm.controls.toFinancialAccountId?.value?.value?.id ?? 0,
            toAccountBalance: 0,
            transferDate: this.app._saveDateTimeWithZone(this.transferFundForm.controls.transferDate.value),
            transferAmount: this.transferFundForm.controls.transferAmount.value,
            memo: this.transferFundForm.controls.memo.value,
            statusId: Status.Active,
            postingDate: this.isAllowPostingPeriod ? this.app._saveDateTimeWithZone(this.updatedPostingPeriodInfo?.postingDate) : this.dialogType == "edit" ? this.transferFundDetails?.postingDate : this.app._saveDateTimeWithZone(this.transferFundForm.controls.transferDate.value) ,
            postingMonth: this.isAllowPostingPeriod ? this.updatedPostingPeriodInfo?.postingMonth : this.dialogType == "edit" ? this.transferFundDetails?.postingMonth : new Date(this.transferFundForm.controls.transferDate.value).getMonth() + 1,
            postingYear: this.isAllowPostingPeriod ? this.updatedPostingPeriodInfo?.postingYear : this.dialogType == "edit" ? this.transferFundDetails?.postingYear : new Date(this.transferFundForm.controls.transferDate.value).getFullYear(),  
        };
        if (this.dialogType !== "edit") {
            this.appMainService.isShowSpinner("savetransferfund");
            this.financeService.saveTransferFund(transferFundObj)
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe({
                    next: (data) => {
                        this.appMainService.isHideSpinner("savetransferfund");
                        this.messageService.add({
                            severity: Severity.Success,
                            summary: this.titlecasePipe.transform(Severity.Success),
                            detail: "Saved Successfully ...",
                        });
                        this.onClose("save");
                    },
                    error: (error) => {
                        this.appMainService.isHideSpinner("savetransferfund");
                        this.messageService.add({
                            severity: Severity.Error,
                            summary: this.titlecasePipe.transform(Severity.Error),
                            detail: "Error occurred ...",
                        });
                    },
                });
        } else {
            this.appMainService.isShowSpinner("updateTransferFund");
            this.financeService.updateTransferFund(transferFundObj)
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe({
                    next: (data) => {
                        this.appMainService.isHideSpinner("updateTransferFund");
                        this.messageService.add({
                            severity: Severity.Success,
                            summary: this.titlecasePipe.transform(Severity.Success),
                            detail: "Update Successfully ...",
                        });
                        this.onClose("save");
                    },
                    error: (error) => {
                        this.appMainService.isHideSpinner("updateTransferFund");
                        this.messageService.add({
                            severity: Severity.Error,
                            summary: this.titlecasePipe.transform(Severity.Error),
                            detail: "Error occurred ...",
                        });
                    },
                });
        }

    }

    getEndingBalance(id) {
        this.appMainService.isShowSpinner("loadendbalance");
        this.financeService.getEndingBalance(id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: (data) => {
                    this.appMainService.isHideSpinner("loadendbalance");
                    this.fromAccountEndingBalance = data;
                },
                error: (error) => {
                    this.appMainService.isHideSpinner("loadendbalance");
                    this.messageService.add({
                        severity: Severity.Error,
                        summary: this.titlecasePipe.transform(Severity.Error),
                        detail: "Error occurred ...",
                    });
                },
            });
    }

    onClose(type): void {
        this.transferFundForm.reset();
        this.close.emit({ type: type });
    }

    voidOrDelete(isVoid: boolean) {
        if (!!this.statementMessage) {
            this.messageService.add({ severity: Severity.Warn, summary: this.titlecasePipe.transform(Severity.Warn), detail: ClosingYearDisplayMessages.voidDeleteMessage });
        } else {
            this.confirmVoidDelete(isVoid);
        }
    }

    confirmVoidDelete(isVoid: boolean) {
        const isVoidStatus = isVoid ? 'Void' : 'Delete'
        this.confirmationService.confirm({
            message: `Do you want to ${isVoidStatus} this Transfer Funds?`,
            header: `${isVoidStatus} Confirmation`,
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.financeService.bankEntriesVoidDelete(this.transferFundId, isVoid, 'RemoveFundTransfer', 'transferId').subscribe(resp => {
                    this.messageService.add({
                        severity: "success",
                        summary: "Success",
                        detail: `${isVoidStatus}${isVoid ? 'ed' : 'd'} Successfully...`,
                    });
                    this.onClose("save");
                })
            },
        });
    }

    onChangeFromAccount(e) {
        this.transferFundForm.controls.toFinancialAccountId.setValue("");
        this.getEndingBalance(e?.node?.value?.id);
        this.disableToAccount(e?.node?.value?.id);
    }

    disableToAccount(id) {
        // let assetData = this.financialAccounts?.find((a) => a?.label?.toLowerCase() == "assets");
        // let bankData = assetData?.children?.find((b) => b?.label?.toLowerCase() == "bank");
        // this.toAccounts = bankData?.children && bankData?.children?.length > 0 ? JSON.parse(JSON.stringify(bankData?.children)) : [];


        this.toAccounts = this.financialAccounts?.filter((a) => this.accountLabels.includes(a.name.toLowerCase()));;
        let foundNode = this.financeUtilityService.findRecursiveChildObjects(this.toAccounts, id);
        foundNode.selectable = false;
    }

    viewToEditFundTransfer() {
        this.dialogType = "edit";
        this.fundTransferHeader = "Edit Transfer Fund";
        this.transferFundForm.enable();
    }

    isClosingStatementDate(event: string) {
        this.statementMessage = event;
    }

   onPostingInfoChange(postingInfo: IPostingPeriodDetails) {
        this.updatedPostingPeriodInfo = postingInfo;
      }
      
}
